import axios from 'axios'
import router from '@/router/index.js'
import { useUserStore } from '@/stores/user.js'

// ── Token Refresh State ─────────────────────────────────────────
let isRefreshing = false
let failedQueue = []

function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// ── Axios Instances ──────────────────────────────────────────────

const appAPI = new axios.create({
  baseURL: `${import.meta.env.VITE_SERVICE_API_URL}/api/v1/app`,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT),
  transformRequest: [ (data, headers) => {
    const userStore = useUserStore()
    if (userStore.authenticated) {
      headers['Authorization'] = `Bearer ${userStore.token}`
    }
    return data
  }, ...axios.defaults.transformRequest ]
})

const authAPI = new axios.create({
  baseURL: `${import.meta.env.VITE_SERVICE_API_URL}/api/v1/auth`,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT),
  transformRequest: [(data, headers) => {
    const userStore = useUserStore()
    if (userStore.authenticated) {
      headers['Authorization'] = `Bearer ${userStore.token}`
    }
    return data
  }, ...axios.defaults.transformRequest ]
})

const sensorsAPI = new axios.create({
  baseURL: `${import.meta.env.VITE_SENSORS_API_URL}/json`,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT)
})

// Separate instance for token refresh to avoid the authAPI 401 interceptor
// (which returns a never‑settling promise on 401, causing hangs)
const refreshAPI = new axios.create({
  baseURL: `${import.meta.env.VITE_SERVICE_API_URL}/api/v1/auth`,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT)
})

// ── Response Interceptors ────────────────────────────────────────

/**
 * Try refreshing the access token via the HttpOnly cookie.
 * The refresh_token cookie is sent automatically by the browser.
 */
async function refreshAccessToken() {
  const response = await refreshAPI.post('/token/refresh/')
  const newToken = response.data.access
  if (newToken) {
    useUserStore().setToken(newToken)
  }
  return newToken
}

// appAPI interceptor: 401 → attempt token refresh → retry
appAPI.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    // Only attempt refresh on 401 and if not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return appAPI(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const newToken = await refreshAccessToken()
        processQueue(null, newToken)
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`
        return appAPI(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Refresh failed – log user out
        useUserStore().clear()
        router.push('/')
        return new Promise(() => {})
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// authAPI interceptor: 401 → clear + redirect (no refresh loop)
authAPI.interceptors.response.use(
  response => response,
  error => {
    if (error.message === 'TIMEOUT') {
      console.warn('AXIOS request timeout')
      return new Promise(() => {})
    }
    if (error.response?.status === 401) {
      useUserStore().clear()
      router.push('/')
      return new Promise(() => {})
    }
    console.error(error.toJSON())
    return Promise.reject(error)
  }
)

export { appAPI, authAPI, sensorsAPI, refreshAPI, refreshAccessToken }
